import type * as THREE from 'three'
import type { GSplatFormat } from '@/plugins/gsplat/lod/GSplatFormat'

export const GSPLAT_TREES_MAX_CELL_SPHERES = 256
export const GSPLAT_TREES_MAX_SPHERES = 65_535
export const GSPLAT_TREES_MAX_GRID_DIM = 64
export const GSPLAT_TREES_MIN_CELL_SIZE = 1e-3
export const GSPLAT_TREES_BIND_STREAM = 'treeBind'

export interface GSplatTreeSphere {
  readonly center: THREE.Vector3
  readonly radius: number
}

export interface GSplatTreesTextureLimit {
  readonly name: string
  readonly actual: number
  readonly limit: number
}

export class GSplatTreesPlanError extends Error {
  readonly code = 'GSPLAT_TREES_PLAN_INVALID'
  readonly limit?: GSplatTreesTextureLimit

  constructor(message: string, limit?: GSplatTreesTextureLimit) {
    super(message)
    this.name = 'GSplatTreesPlanError'
    this.limit = limit
  }
}

export interface GSplatTreesPlan {
  readonly spheres: readonly GSplatTreeSphere[]
  readonly sphereData: Float32Array
  readonly gridData: Float32Array
  readonly listData: Float32Array
  readonly gridMin: readonly [number, number]
  readonly gridInvCell: readonly [number, number]
  readonly gridDims: readonly [number, number]
  readonly listCount: number
}

/**
 * Add the permanent per-instance bind stream to one format identity.
 *
 * `GSplatFormat` itself deduplicates extra streams and owns the revision bump;
 * this helper additionally rejects an incompatible pre-existing declaration
 * instead of silently reusing a resource/stream with a different layout.
 */
export function ensureGSplatTreesFormat(format: GSplatFormat): void {
  const existing = format.getStream(GSPLAT_TREES_BIND_STREAM)
  if (existing) {
    if (existing.format !== 'rgba8unorm' || existing.storage !== 'instance') {
      throw new GSplatTreesPlanError(
        `Trees treeBind stream 布局冲突: format=${existing.format}, storage=${existing.storage ?? 'resource'}`
      )
    }
    return
  }
  format.addExtraStreams([
    {
      name: GSPLAT_TREES_BIND_STREAM,
      format: 'rgba8unorm',
      storage: 'instance',
    },
  ])
}

/**
 * Build the immutable CPU payload consumed by the Trees copy shader.
 *
 * PlayCanvas buckets XZ footprints. SHARE3DEngine's Z-up convention uses XY as
 * the horizontal plane and Z as the vertical axis, so the planner stores the
 * first two center components in the grid and leaves the full sphere center in
 * the RGBA32F sphere texture.
 */
export function buildGSplatTreesPlan(
  input: readonly GSplatTreeSphere[],
  limits: readonly GSplatTreesTextureLimit[] = []
): GSplatTreesPlan {
  const spheres = cloneAndValidateSpheres(input)
  if (spheres.length > GSPLAT_TREES_MAX_SPHERES) {
    throw new GSplatTreesPlanError(
      `Trees sphere 数量超过 16-bit tree index 上限: actual=${spheres.length}, limit=${GSPLAT_TREES_MAX_SPHERES}`,
      {
        name: 'sphereCount',
        actual: spheres.length,
        limit: GSPLAT_TREES_MAX_SPHERES,
      }
    )
  }

  const dim = Math.min(
    GSPLAT_TREES_MAX_GRID_DIM,
    Math.max(1, Math.ceil(Math.sqrt(Math.max(1, spheres.length))))
  )
  const bounds = resolveGridBounds(spheres)
  const cellWidth = Math.max((bounds.maxX - bounds.minX) / dim, GSPLAT_TREES_MIN_CELL_SIZE)
  const cellHeight = Math.max((bounds.maxY - bounds.minY) / dim, GSPLAT_TREES_MIN_CELL_SIZE)
  const cells: number[][] = Array.from({ length: dim * dim }, () => [])

  for (let index = 0; index < spheres.length; index += 1) {
    const sphere = spheres[index]
    const minX = clamp(
      Math.floor((sphere.center.x - sphere.radius - bounds.minX) / cellWidth),
      0,
      dim - 1
    )
    const maxX = clamp(
      Math.floor((sphere.center.x + sphere.radius - bounds.minX) / cellWidth),
      0,
      dim - 1
    )
    const minY = clamp(
      Math.floor((sphere.center.y - sphere.radius - bounds.minY) / cellHeight),
      0,
      dim - 1
    )
    const maxY = clamp(
      Math.floor((sphere.center.y + sphere.radius - bounds.minY) / cellHeight),
      0,
      dim - 1
    )

    for (let row = minY; row <= maxY; row += 1) {
      for (let column = minX; column <= maxX; column += 1) {
        const cell = cells[row * dim + column]
        cell.push(index)
        if (cell.length > GSPLAT_TREES_MAX_CELL_SPHERES) {
          throw new GSplatTreesPlanError(
            `Trees grid cell 超过 copy shader 上限: actual=${cell.length}, limit=${GSPLAT_TREES_MAX_CELL_SPHERES}`,
            {
              name: 'cellSphereCount',
              actual: cell.length,
              limit: GSPLAT_TREES_MAX_CELL_SPHERES,
            }
          )
        }
      }
    }
  }

  const listCount = cells.reduce((total, cell) => total + cell.length, 0)
  const textureSizes: readonly GSplatTreesTextureLimit[] = [
    { name: 'sphereTextureWidth', actual: Math.max(1, spheres.length), limit: Infinity },
    { name: 'gridTextureWidth', actual: dim, limit: GSPLAT_TREES_MAX_GRID_DIM },
    { name: 'gridTextureHeight', actual: dim, limit: GSPLAT_TREES_MAX_GRID_DIM },
    { name: 'listTextureWidth', actual: Math.max(1, listCount), limit: Infinity },
    ...limits,
  ]
  for (const textureSize of textureSizes) {
    if (Number.isFinite(textureSize.limit) && textureSize.actual > textureSize.limit) {
      throw new GSplatTreesPlanError(
        `Trees ${textureSize.name} 超过设备限制: actual=${textureSize.actual}, limit=${textureSize.limit}`,
        textureSize
      )
    }
  }

  const sphereData = new Float32Array(Math.max(1, spheres.length) * 4)
  for (let index = 0; index < spheres.length; index += 1) {
    const sphere = spheres[index]
    const offset = index * 4
    sphereData[offset] = sphere.center.x
    sphereData[offset + 1] = sphere.center.y
    sphereData[offset + 2] = sphere.center.z
    sphereData[offset + 3] = sphere.radius
  }

  const gridData = new Float32Array(dim * dim * 4)
  const listData = new Float32Array(Math.max(1, listCount) * 4)
  let listOffset = 0
  for (let cellIndex = 0; cellIndex < cells.length; cellIndex += 1) {
    const cell = cells[cellIndex]
    const gridOffset = cellIndex * 4
    gridData[gridOffset] = listOffset
    gridData[gridOffset + 1] = cell.length
    for (const sphereIndex of cell) {
      listData[listOffset * 4] = sphereIndex
      listOffset += 1
    }
  }

  return Object.freeze({
    spheres,
    sphereData,
    gridData,
    listData,
    gridMin: Object.freeze([bounds.minX, bounds.minY] as [number, number]),
    gridInvCell: Object.freeze([1 / cellWidth, 1 / cellHeight] as [number, number]),
    gridDims: Object.freeze([dim, dim] as [number, number]),
    listCount,
  })
}

/** The deterministic equivalent of the upstream reversed-edge smoothstep. */
export function gsplatTreesEdgeFade(distance: number, radius: number): number {
  if (!Number.isFinite(distance) || !Number.isFinite(radius) || radius <= 0) return 0
  const t = clamp((distance - radius * 0.8) / (radius * 0.2), 0, 1)
  return 1 - t * t * (3 - 2 * t)
}

/** Pack a zero-based tree index into the 1-based RGBA8 bind representation. */
export function packGSplatTreeIndex(index: number): readonly [number, number] {
  if (!Number.isSafeInteger(index) || index < 0 || index >= GSPLAT_TREES_MAX_SPHERES) {
    throw new GSplatTreesPlanError(`Trees index 非法: ${index}`)
  }
  const oneBased = index + 1
  return [oneBased % 256, Math.floor(oneBased / 256)]
}

export function unpackGSplatTreeIndex(low: number, high: number): number {
  const oneBased = Math.round(low) + Math.round(high) * 256
  if (oneBased <= 0 || oneBased > GSPLAT_TREES_MAX_SPHERES) return -1
  return oneBased - 1
}

function cloneAndValidateSpheres(input: readonly GSplatTreeSphere[]): readonly GSplatTreeSphere[] {
  return Object.freeze(
    input.map((sphere, index) => {
      const center = sphere.center.clone()
      if (
        !Number.isFinite(center.x) ||
        !Number.isFinite(center.y) ||
        !Number.isFinite(center.z) ||
        !Number.isFinite(sphere.radius) ||
        sphere.radius <= 0
      ) {
        throw new GSplatTreesPlanError(`Trees sphere[${index}] center/radius 非法`)
      }
      return Object.freeze({ center, radius: sphere.radius })
    })
  )
}

function resolveGridBounds(spheres: readonly GSplatTreeSphere[]): {
  minX: number
  minY: number
  maxX: number
  maxY: number
} {
  if (spheres.length === 0) {
    return { minX: 0, minY: 0, maxX: 1, maxY: 1 }
  }
  let minX = Infinity
  let minY = Infinity
  let maxX = -Infinity
  let maxY = -Infinity
  for (const sphere of spheres) {
    minX = Math.min(minX, sphere.center.x - sphere.radius)
    minY = Math.min(minY, sphere.center.y - sphere.radius)
    maxX = Math.max(maxX, sphere.center.x + sphere.radius)
    maxY = Math.max(maxY, sphere.center.y + sphere.radius)
  }
  return { minX, minY, maxX, maxY }
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value))
}
